type StorageType = 'session' | 'cookie';

type CookieOptions = {
  days?: number;
  path?: string;
  secure?: boolean;
  sameSite?: 'strict' | 'lax' | 'none';
};

// Private helper functions
const getCookieValue = <T>(storageKey: string): T | null => {
  try {
    const value = `; ${document.cookie}`;
    const parts = value.split(`; ${storageKey}=`);
    if (parts.length === 2) {
      const cookieValue = parts.pop()?.split(';').shift();
      return cookieValue ? JSON.parse(decodeURIComponent(cookieValue)) : null;
    }
    return null;
  } catch (error) {
    console.warn(`Error parsing cookie value for key: ${storageKey}`, error);
    return null;
  }
};

const getSessionValue = <T>(storageKey: string): T | null => {
  try {
    const value = sessionStorage.getItem(storageKey);
    return value ? JSON.parse(value) : null;
  } catch (error) {
    console.warn(`Error parsing session value for key: ${storageKey}`, error);
    return null;
  }
};

const setCookieValue = (
  storageKey: string,
  value: unknown,
  options: CookieOptions
): boolean => {
  try {
    const {
      days = 365,
      path = '/',
      secure = false,
      sameSite = 'lax',
    } = options;

    const date = new Date();
    date.setTime(date.getTime() + days * 24 * 60 * 60 * 1000);

    const expires = `expires=${date.toUTCString()}`;
    const pathAttr = `path=${path}`;
    const secureAttr = secure ? 'secure' : '';
    const sameSiteAttr = `samesite=${sameSite}`;

    document.cookie = [
      `${storageKey}=${encodeURIComponent(JSON.stringify(value))}`,
      expires,
      pathAttr,
      secureAttr,
      sameSiteAttr,
    ]
      .filter(Boolean)
      .join('; ');
    return true;
  } catch (error) {
    console.warn(`Error setting cookie for key: ${storageKey}`, error);
    return false;
  }
};

const setSessionValue = (storageKey: string, value: unknown): boolean => {
  try {
    sessionStorage.setItem(storageKey, JSON.stringify(value));
    return true;
  } catch (error) {
    console.warn(`Error setting session value for key: ${storageKey}`, error);
    return false;
  }
};

const removeCookie = (storageKey: string): boolean => {
  try {
    document.cookie = `${storageKey}=; expires=Thu, 01 Jan 1970 00:00:00 UTC; path=/`;
    return true;
  } catch (error) {
    console.warn(`Error removing cookie for key: ${storageKey}`, error);
    return false;
  }
};

const removeSession = (storageKey: string): boolean => {
  try {
    sessionStorage.removeItem(storageKey);
    return true;
  } catch (error) {
    console.warn(`Error removing session value for key: ${storageKey}`, error);
    return false;
  }
};

// Factory function to create storage helper with prefix
export const createStorageHelper = (prefix: string) => {
  const getStorageKey = (key: string) => `${prefix}_${key}`;

  return {
    /**
     * Gets a value from storage (cookie or session)
     * @param key - The key to retrieve
     * @param storageType - Where to retrieve from ('cookie' or 'session')
     * @returns The parsed value or null if not found/error
     */
    getValue: <T = unknown>(
      key: string,
      storageType: StorageType
    ): T | null => {
      const storageKey = getStorageKey(key);

      if (storageType === 'cookie') {
        return getCookieValue<T>(storageKey);
      } else if (storageType === 'session') {
        return getSessionValue<T>(storageKey);
      }

      return null;
    },

    /**
     * Sets a value in storage (cookie or session)
     * @param key - The key to set
     * @param value - The value to store
     * @param storageType - Where to store ('cookie' or 'session')
     * @param options - Additional options for cookies
     * @returns Success boolean
     */
    setValue: (
      key: string,
      value: unknown,
      storageType: StorageType,
      options: CookieOptions = {}
    ): boolean => {
      const storageKey = getStorageKey(key);

      if (storageType === 'cookie') {
        return setCookieValue(storageKey, value, options);
      } else if (storageType === 'session') {
        return setSessionValue(storageKey, value);
      }

      return false;
    },

    /**
     * Removes a value from storage
     * @param key - The key to remove
     * @param storageType - Where to remove from
     * @returns Success boolean
     */
    removeValue: (key: string, storageType: StorageType): boolean => {
      const storageKey = getStorageKey(key);

      if (storageType === 'cookie') {
        return removeCookie(storageKey);
      } else if (storageType === 'session') {
        return removeSession(storageKey);
      }

      return false;
    },
  };
};

// Alternative: Individual functions if you don't need the prefix functionality
export const storageUtils = {
  getValue: <T = unknown>(key: string, storageType: StorageType): T | null => {
    if (storageType === 'cookie') {
      return getCookieValue<T>(key);
    } else if (storageType === 'session') {
      return getSessionValue<T>(key);
    }
    return null;
  },

  setValue: (
    key: string,
    value: unknown,
    storageType: StorageType,
    options: CookieOptions = {}
  ): boolean => {
    if (storageType === 'cookie') {
      return setCookieValue(key, value, options);
    } else if (storageType === 'session') {
      return setSessionValue(key, value);
    }
    return false;
  },

  removeValue: (key: string, storageType: StorageType): boolean => {
    if (storageType === 'cookie') {
      return removeCookie(key);
    } else if (storageType === 'session') {
      return removeSession(key);
    }
    return false;
  },
};

export { type StorageType, type CookieOptions };
